import { FetchResult } from "@apollo/client";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useStepperContext } from "src/components/stepper";
import { NextStepButton } from "src/components/stepper/useStepperWizard/NextStepButton";
import { SavePlanPackageDetailsMutation } from "src/routes/libraries/plan-package/stepper/1.PlanDetailsStep.generated";
import { PlanPackage_SaveReadyPlanOptionsMutation } from "src/routes/libraries/plan-package/stepper/4.ConfigureOptionsStep.generated";
import { createPlanPackageEditUrl, createPlanPackageUrl } from "src/RouteUrls";

type PlanPackageNextStepButtonProps = {
  planId: string | undefined;
  versionId: string | undefined;
  disabled?: boolean;
  lastStep?: boolean;
  onSave: () => Promise<
    FetchResult<SavePlanPackageDetailsMutation | PlanPackage_SaveReadyPlanOptionsMutation> | undefined
  >;
};

export function PlanPackageNextStepButton({
  planId,
  versionId,
  disabled,
  lastStep,
  onSave,
}: PlanPackageNextStepButtonProps) {
  const navigate = useNavigate();
  const { nextStepEvenIfDisabled } = useStepperContext();

  const onContinue = useCallback(async () => {
    const response = await onSave();
    if (response?.data && versionId !== response.data.savePlanPackage.planPackage.version.id) {
      const pp = response.data.savePlanPackage.planPackage;
      navigate(createPlanPackageEditUrl(pp.id, pp.version.id));
      nextStepEvenIfDisabled();
    }
  }, [onSave, versionId, navigate, nextStepEvenIfDisabled]);

  const onSaveAndExit = useCallback(async () => {
    const response = await onSave();
    const pp = response?.data?.savePlanPackage.planPackage;
    if (pp) {
      navigate(createPlanPackageUrl(pp.id, pp.version.id));
    } else if (planId && versionId) {
      navigate(createPlanPackageUrl(planId, versionId));
    }
  }, [navigate, onSave, planId, versionId]);

  return (
    <NextStepButton
      label={lastStep ? "Finish" : "Continue"}
      disabled={disabled}
      onClick={lastStep ? onSaveAndExit : onContinue}
      exitButton={{ variant: "secondary", label: "Save & Exit", onClick: onSaveAndExit }}
    />
  );
}
